/**
 * Reconnects device on triggers:
 * - initial: once on mount when online (offline at mount: shows cached assets and waits for online)
 * - visibility: when user returns to the app
 *
 * Reconnects are skipped while onboarding is in progress.
 */

import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';

import damDeviceService from '../services/dam/damDeviceService';
import damService from '../services/dam/damService';
import {notifyKitConnectionResult} from '../services/device/deviceKitNotificationService';
import log from '../services/logger/log';
import storageService from '../services/storageService';
import {selectIsOnline} from '../state/selectors/global';
import {selectIsOnboardingInProgress} from '../state/selectors/session';
import {getState} from '../state/store';
import useAppVisibilityChange from './useAppVisibilityChange';

const useReconnectDevice = (dusn) => {
    const isOnboardingInProgress = useSelector(selectIsOnboardingInProgress);
    const isOnline = useSelector(selectIsOnline);
    const isInitialConnectionRef = useRef(true);

    const startReconnect = async (isInitialConnection, trigger) => {
        // getState() instead of useSelector: useAppVisibilityChange fires outside React render — need fresh state
        const state = getState();

        if (selectIsOnboardingInProgress(state)) {
            log.debug(`useReconnectDevice: Skip reconnect, dusn: ${dusn}, Onboarding in progress.`);
            return;
        }

        try {
            if (isInitialConnection) {
                log.debug(`useReconnectDevice: Load cached assets. trigger: ${trigger}, dusn: ${dusn}`);
                await damService.setDeviceAssets(dusn, false, true);
            }

            if (!selectIsOnline(state)) {
                log.debug(`useReconnectDevice: Skip reconnect, dusn: ${dusn}, Offline.`);
                return;
            }

            log.info(`useReconnectDevice: Start connecting device. trigger: ${trigger}, dusn: ${dusn}`);

            await damDeviceService.reconnectDevice(dusn);

            if (isInitialConnection) {
                notifyKitConnectionResult(dusn);
            }

            log.info(`useReconnectDevice: Device connected successfully. trigger: ${trigger}, dusn: ${dusn}`);
        } catch (e) {
            log.info(`useReconnectDevice: End connecting device. Failed. trigger: ${trigger}, dusn: ${dusn}`);
        }
    };

    useAppVisibilityChange({
        onVisible: () => {
            // For testing purposes only
            if (storageService.getIsVisibilityReconnectDisabled()) {
                log.debug(`useReconnectDevice: Visibility reconnect disabled, dusn: ${dusn}`);
                return;
            }

            startReconnect(false, 'visibility');
        },
    });

    const startInitialReconnect = () => {
        if (!isInitialConnectionRef.current) return;

        startReconnect(true, 'initial');

        // offline only cached assets are loaded, the reconnect itself must run again once online
        if (isOnline) isInitialConnectionRef.current = false;
    };

    useEffect(startInitialReconnect, [isOnline, isOnboardingInProgress]);
};

export default useReconnectDevice;
